/**
 * BotSpot Web Dashboard Hatchery API
 *
 * API functions for agent hatching and persona gallery.
 */

import { apiClient } from "./client";
import type {
  DemoHatchRequest,
  HatchResponse,
  GalleryResponse,
  AgentIdentityPublic,
} from "./types";

/**
 * Hatch a new demo agent
 *
 * Generates a new agent identity with a unique persona profile.
 * This is a public endpoint that does not require authentication.
 *
 * @param creatorName - Name of the creator hatching the agent
 * @returns Hatched agent with persona, soul, and cryptographic identity
 */
export async function hatchAgent(creatorName: string): Promise<HatchResponse> {
  const requestData: DemoHatchRequest = {
    creator_name: creatorName,
  };
  return await apiClient.post<HatchResponse>(
    "/hatchery/demo/hatch",
    requestData
  );
}

/**
 * Get gallery of recently hatched agents
 *
 * Retrieves a paginated list of recently hatched agent personas.
 * This is a public endpoint that does not require authentication.
 *
 * @param limit - Maximum number of agents to return (1-100, default 20)
 * @returns Gallery response with array of agent identities
 */
export async function getGallery(limit: number = 20): Promise<GalleryResponse> {
  const queryParams = new URLSearchParams();
  queryParams.append("limit", Math.max(1, Math.min(100, limit)).toString());

  const url = `/hatchery/demo/gallery?${queryParams.toString()}`;
  return await apiClient.get<GalleryResponse>(url);
}

/**
 * Get a specific agent identity by ID
 *
 * Note: This endpoint may require authentication in the future.
 * This is a placeholder for future implementation.
 *
 * @param agentId - Agent ID
 * @returns Agent identity with persona and block proof
 */
export async function getAgentIdentity(
  agentId: string
): Promise<AgentIdentityPublic> {
  return await apiClient.get<AgentIdentityPublic>(`/hatchery/agents/${agentId}`);
}
